import React, { useState } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Box,
  FormHelperText,
  Typography,
} from "@mui/material";
import { useMenuScrollPagination } from "../../hooks/useMenuScrollPagination";

interface Option {
  label: string;
  value: string | number;
}

interface AsyncAutoCompleteProps {
  label: string;
  value: string | number | null;
  onChange: (value: any) => void;
  options: Option[];
  onSearch: (text: string) => void;
  onScrollEnd?: () => void;
  loading?: boolean;
  error?: boolean;
  helperText?: string | null;
  required?: boolean;
  disabled?: boolean;
  // Pass the currently selected item explicitly so it always renders
  // even if it hasn't been loaded into the paginated options list yet
  currentOption?: Option | null;
  totalCount?: number;
}

export const AsyncAutoComplete: React.FC<AsyncAutoCompleteProps> = ({
  label,
  value,
  onChange,
  options,
  onSearch,
  onScrollEnd,
  loading = false,
  error,
  helperText,
  required,
  disabled,
  currentOption,
  totalCount,
}) => {
  const [search, setSearch] = useState("");

  const { handleMenuOpen, handleMenuClose } = useMenuScrollPagination({
    onSearch,
    onScrollEnd,
    options,
    search,
  });

  const handleClose = () => {
    setSearch("");
    onSearch("");
    handleMenuClose();
  };

  // ─── Merge currentOption into options list ────────────────────────────────
  // If the selected value isn't in the current paginated options (e.g. it's on
  // a page not yet loaded), inject it so MUI can render its label correctly.
  const mergedOptions: Option[] = React.useMemo(() => {
    if (!currentOption || !value) return options;
    const alreadyInList = options.some((o) => o.value === currentOption.value);
    if (alreadyInList) return options;
    // Prepend so it's visible at top of list
    return [currentOption, ...options];
  }, [options, currentOption, value]);

  return (
    <FormControl fullWidth error={error} required={required} disabled={disabled}>
      <InputLabel>{label}</InputLabel>

      <Select
        value={value ?? ""}
        label={label}
        disabled={disabled}
        onChange={(e) => !disabled && onChange(e.target.value)}
        onOpen={handleMenuOpen}
        onClose={handleClose}
        MenuProps={{
          disableScrollLock: true,
          PaperProps: {
            sx: {
              maxHeight: 400,
              overflowY: "auto",
              overflowX: "hidden",
            },
          },
        }}
      >
        {!disabled && (
          <Box
            p={1}
            position="sticky"
            top={0}
            zIndex={1}
            bgcolor="background.paper"
            onClick={(e) => e.stopPropagation()}
          >
            <TextField
              placeholder="Search..."
              size="small"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              autoFocus
            />
            {totalCount !== undefined && (
              <Box display="flex" justifyContent="flex-end" mt={0.5} px={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Total options: {totalCount}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {mergedOptions.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}

        {loading && (
          <MenuItem
            disabled
            sx={{
              position: "sticky",
              bottom: 0,
              bgcolor: "background.paper",
              justifyContent: "center",
              py: 1,
            }}
          >
            <CircularProgress size={18} sx={{ mr: 1 }} />
            Loading more...
          </MenuItem>
        )}

        {!loading && mergedOptions.length === 0 && !disabled && (
          <MenuItem disabled>
            <em>No options available</em>
          </MenuItem>
        )}

        {disabled && (
          <MenuItem disabled>
            <em>Select a Region first</em>
          </MenuItem>
        )}
      </Select>

      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};